import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BOOKING_URL } from "../lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeadCapture({ t, lang, sessionId }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [attach, setAttach] = useState(true);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        lang,
        source: "twin-site",
        session_id: sessionId || null,
        include_transcript: Boolean(sessionId && attach),
      });
      setDone(true);
      toast.success(t.lead.success);
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.lead.fail);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="lead"
      data-testid="lead-section"
      className="bg-ember text-paper border-b border-ink"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-paper">
            ✷ {t.lead.kicker}
          </div>
          <h2 className="mt-4 font-serif text-5xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight">
            {t.lead.title_1}
            <br />
            <span className="italic">{t.lead.title_2}</span>
          </h2>
          <p className="mt-8 max-w-md text-lg text-paper leading-relaxed">{t.lead.body}</p>

          <a
            data-testid="lead-booking-link"
            href={BOOKING_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-10 inline-flex items-center gap-3 px-6 py-3 bg-paper text-ink rounded-full font-mono text-[12px] tracking-[0.18em] uppercase hover:bg-ink hover:text-paper transition-colors"
          >
            <span>📅</span>
            {t.lead.booking}
          </a>
        </div>

        <form
          data-testid="lead-form"
          onSubmit={submit}
          className="lg:col-span-6 lg:col-start-7 bg-paper text-ink p-6 md:p-10 rounded-[2px] border border-ink shadow-[8px_8px_0_0_#1A1A1A]"
        >
          {done ? (
            <div data-testid="lead-success" className="py-12 text-center">
              <div className="font-serif text-4xl">✓</div>
              <p className="mt-6 font-serif text-2xl leading-snug">{t.lead.success}</p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 font-mono text-[11px] tracking-[0.2em] uppercase underline underline-offset-4"
              >
                {lang === "no" ? "send en til" : "send another"}
              </button>
            </div>
          ) : (
            <>
              <Field
                label={t.lead.name}
                testId="lead-name"
                value={form.name}
                onChange={change("name")}
                required
              />
              <Field
                label={t.lead.email}
                testId="lead-email"
                type="email"
                value={form.email}
                onChange={change("email")}
                required
              />
              <Field
                label={t.lead.message}
                testId="lead-message"
                value={form.message}
                onChange={change("message")}
                required
                textarea
              />

              {sessionId && (
                <label data-testid="lead-attach-transcript" className="flex items-start gap-3 mt-1 cursor-pointer select-none">
                  <input
                    data-testid="lead-attach-checkbox"
                    type="checkbox"
                    checked={attach}
                    onChange={(e) => setAttach(e.target.checked)}
                    className="mt-1 h-4 w-4 accent-[#D35400]"
                  />
                  <span className="text-sm leading-snug">
                    {lang === "no"
                      ? "Legg ved samtalen min med tvillingen, så Nickolass ser hva vi snakket om."
                      : "Attach my chat with the twin so Nickolass sees what we talked about."}
                  </span>
                </label>
              )}

              <button
                type="submit"
                data-testid="lead-submit-btn"
                disabled={sending}
                className="mt-6 w-full bg-ink text-paper py-4 font-mono text-[12px] tracking-[0.22em] uppercase rounded-sm hover:bg-ember transition-colors disabled:opacity-50"
              >
                {sending ? "…" : `${t.lead.submit} →`}
              </button>

              <p className="mt-4 font-mono text-[10px] tracking-[0.18em] uppercase text-mist leading-relaxed">
                {t.lead.consent}{" "}
                <Link
                  data-testid="lead-privacy-link"
                  to="/personvern"
                  className="text-ink underline underline-offset-2 hover:text-ember"
                >
                  {t.lead.consent_link}
                </Link>
                {t.lead.consent_after}
              </p>
            </>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, testId, value, onChange, type = "text", required, textarea }) {
  return (
    <label className="block mb-5">
      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-mist">{label}</span>
      {textarea ? (
        <textarea
          data-testid={testId}
          rows={4}
          value={value}
          onChange={onChange}
          required={required}
          className="mt-2 w-full bg-transparent border-b border-ink py-2 outline-none focus:border-terracotta transition-colors text-base resize-none"
        />
      ) : (
        <input
          data-testid={testId}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          className="mt-2 w-full bg-transparent border-b border-ink py-2 outline-none focus:border-terracotta transition-colors text-base"
        />
      )}
    </label>
  );
}
